<script setup>
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import PeriodSelector from '@/Components/UI/PeriodSelector.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import TopVehiclesChart from '@/Components/Reports/TopVehiclesChart.vue'
import VehicleRevenueCard from '@/Components/Reports/VehicleRevenueCard.vue'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

const props = defineProps({
  rows:            { type: Array,  default: () => [] },
  totals:          { type: Object, default: () => ({}) },
  filters:         { type: Object, default: () => ({}) },
  period:          { type: String, default: 'this_month' },
  period_label:    { type: String, default: '' },
  period_from:     { type: String, default: '' },
  period_to:       { type: String, default: '' },
  vehicle_classes: { type: Array,  default: () => [] }
})

const { t } = useI18n()

const groupBy = ref(props.filters.group_by || 'per_vehicle')
const ownership = ref(props.filters.ownership || 'all')
const vehicleClass = ref(props.filters.vehicle_class || 'all')
const q = ref(props.filters.q || '')

function navigate(patch) {
  router.get('/reports/vehicles', { ...props.filters, ...patch }, {
    preserveState: true, preserveScroll: true
  })
}

function setGroupBy(v)    { groupBy.value    = v; navigate({ group_by: v }) }
function setOwnership(v)  { ownership.value  = v; navigate({ ownership: v === 'all' ? null : v }) }
function setClass(v)      { vehicleClass.value = v; navigate({ vehicle_class: v === 'all' ? null : v }) }
function applySearch()    { navigate({ q: q.value || null }) }

const groupByOptions = computed(() => [
  { value: 'per_vehicle', label: t('reports.vehicles.groupBy.per_vehicle') },
  { value: 'class',       label: t('reports.vehicles.groupBy.class') },
  { value: 'subtype',     label: t('reports.vehicles.groupBy.subtype') }
])

const ownershipOptions = computed(() => [
  { value: 'all',        label: t('vehicles.ownership.all') },
  { value: 'owned',      label: t('vehicles.ownership.owned') },
  { value: 'sub_rented', label: t('vehicles.ownership.sub_rented') }
])

const classOptions = computed(() => [
  { value: 'all', label: t('vehicles.classes.all') },
  ...props.vehicle_classes.map(c => ({ value: c, label: t(`vehicles.classes.${c}`) }))
])

const tableColumns = computed(() => {
  const isPerVehicle = groupBy.value === 'per_vehicle'
  const cols = []
  cols.push({
    key: isPerVehicle ? 'registration' : 'label',
    label: t(isPerVehicle ? 'reports.vehicles.cols.vehicle' : 'reports.vehicles.cols.group')
  })
  cols.push({ key: 'rentals_count',   label: t('reports.vehicles.cols.rentals'),     align: 'right' })
  cols.push({ key: 'days_rented',     label: t('reports.vehicles.cols.days'),        align: 'right' })
  cols.push({ key: 'gross',           label: t('reports.vehicles.cols.gross'),       align: 'right' })
  cols.push({ key: 'losses',          label: t('reports.vehicles.cols.losses'),      align: 'right' })
  cols.push({ key: 'sub_rent_cost',   label: t('reports.vehicles.cols.sub_rent'),    align: 'right' })
  cols.push({ key: 'net',             label: t('reports.vehicles.cols.net'),         align: 'right' })
  cols.push({ key: 'utilization_pct', label: t('reports.vehicles.cols.utilization'), align: 'right' })
  cols.push({ key: 'avg_daily',       label: t('reports.vehicles.cols.avg_daily'),   align: 'right' })
  return cols
})

const isPerVehicle = computed(() => groupBy.value === 'per_vehicle')
</script>

<template>
  <PageShell
    :title="t('reports.vehicles.title')"
    :subtitle="period_label"
    back-href="/"
    :back-label="t('common.back')"
  >
    <template #filters>
      <PeriodSelector
        :current="period"
        :from="period_from"
        :to="period_to"
      />
      <UiSegmented
        :model-value="groupBy"
        :options="groupByOptions"
        @update:model-value="setGroupBy"
      />
      <UiSegmented
        :model-value="vehicleClass"
        :options="classOptions"
        @update:model-value="setClass"
      />
      <UiSegmented
        :model-value="ownership"
        :options="ownershipOptions"
        @update:model-value="setOwnership"
      />
      <UiInput
        v-model="q"
        :placeholder="t('reports.vehicles.searchPlaceholder')"
        class="md:w-64"
        @keyup.enter="applySearch"
      />
    </template>

    <UiCard>
      <div class="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.rentals') }}
          </div>
          <div class="fw-value tabular-nums">
            {{ totals.rentals_count }}
          </div>
        </div>
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.days') }}
          </div>
          <div class="fw-value tabular-nums">
            {{ totals.days_rented }}
          </div>
        </div>
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.gross') }}
          </div>
          <div class="fw-value tabular-nums">
            {{ totals.gross }}
          </div>
        </div>
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.losses') }}
          </div>
          <div class="fw-value tabular-nums">
            {{ totals.losses }}
          </div>
        </div>
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.sub_rent') }}
          </div>
          <div class="fw-value tabular-nums">
            {{ totals.sub_rent_cost }}
          </div>
        </div>
        <div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ t('reports.vehicles.cols.net') }}
          </div>
          <div
            class="fw-value tabular-nums"
            :class="totals.net_cents < 0 ? 'text-[color:var(--danger)]' : ''"
          >
            {{ totals.net }}
          </div>
        </div>
      </div>
    </UiCard>

    <TopVehiclesChart
      v-if="isPerVehicle"
      :rows="rows"
    />

    <div v-if="rows.length === 0">
      <EmptyState type="filter">
        {{ t('reports.vehicles.empty') }}
      </EmptyState>
    </div>
    <template v-else>
      <div class="hidden md:block">
        <UiTable
          :rows="rows"
          :columns="tableColumns"
          :row-key="(r) => r.id || r.label"
          :row-href="(r) => r.id ? `/vehicles/${r.id}` : null"
        >
          <template #cell-registration="{ row }">
            <div>
              <div class="font-semibold">
                {{ row.registration }}
              </div>
              <div class="text-xs text-[color:var(--muted)]">
                {{ row.make }} {{ row.model }}
              </div>
            </div>
          </template>
          <template #cell-label="{ row }">
            <UiStatusPill
              tone="muted"
              class="capitalize"
              :label="row.label"
            />
          </template>
          <template #cell-utilization_pct="{ row }">
            <span class="tabular-nums">{{ row.utilization_pct }}%</span>
          </template>
        </UiTable>
      </div>
      <div class="md:hidden">
        <VehicleRevenueCard
          v-for="r in rows"
          :key="r.id || r.label"
          :row="r"
        />
      </div>
    </template>
  </PageShell>
</template>
